// src/routes/Landing.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Grid,
  Box,
  Card,
  Paper,
  useMediaQuery,
  TextField,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { bg, text as textColors, border, shadow, overlay, statusBg, adaptiveColor } from '../../utils/darkModeAdapter';

import Footer from '../../components/layout/Footer';
import topImage from '../../assets/images/daredemojinji.top.gif';

// アイコン
import AssignmentIcon from '@mui/icons-material/Assignment';
import CampaignIcon from '@mui/icons-material/Campaign';
import DescriptionIcon from '@mui/icons-material/Description';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import WorkIcon from '@mui/icons-material/Work';
import InfoIcon from '@mui/icons-material/Info';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import CategoryIcon from '@mui/icons-material/Category';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// アニメーション (Framer Motion)
import { motion } from 'framer-motion';

// axios
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_API_URL;

// 金額を日本語形式にフォーマットする関数
const formatCurrency = (value) => {
  if (!value || isNaN(value)) return '';
  const num = parseInt(value);
  if (num >= 100000000) {
    const oku = Math.floor(num / 100000000);
    const man = Math.floor((num % 100000000) / 10000);
    if (man === 0) {
      return `${oku}億円`;
    } else {
      return `${oku}億${man}万円`;
    }
  } else if (num >= 10000) {
    const man = Math.floor(num / 10000);
    const remainder = num % 10000;
    if (remainder === 0) {
      return `${man}万円`;
    } else {
      return `${man}万${remainder}円`;
    }
  } else {
    return `${num}円`;
  }
};

// 人数を日本語形式にフォーマットする関数
const formatEmployee = (value) => {
  if (!value || isNaN(value)) return '';
  const num = parseInt(value);
  if (num >= 10000) {
    const man = Math.floor(num / 10000);
    const remainder = num % 10000;
    if (remainder === 0) {
      return `${man}万人`;
    } else {
      return `${man}万${remainder}人`;
    }
  } else {
    return `${num}人`;
  }
};

// 数値のみの入力を許可するハンドラ
const handleNumericInput = (e, setter) => {
  const value = e.target.value.replace(/[^0-9]/g, ''); // 数値以外を除去
  setter(value);
};

// フェードイン用 (アニメーション設定)
const fadeIn = (delay = 0, duration = 0.6) => ({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { delay, duration },
});

function Landing() {
  const navigate = useNavigate();
  const isSmDown = useMediaQuery('(max-width:600px)');
  const theme = useTheme();

  // ======================
  // (A) すでにプロフィールがあればスキップ
  // ======================
  const [profileExists, setProfileExists] = useState(false);
  const [loading, setLoading] = useState(true);

  // 「基本情報を入力」ボタンを押したかどうか
  const [showForm, setShowForm] = useState(false);

  // 最終的なフォーム送信が完了したかどうか
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  // ロック機能クリック時のモーダル管理
  const [lockedFeatureModal, setLockedFeatureModal] = useState({
    open: false,
    featureName: '',
  });

  // ステップ管理用 (Stepper)
  const [activeStep, setActiveStep] = useState(0);

  // ★ エラーメッセージ (必須項目が空のときに表示)
  const [errorMsg, setErrorMsg] = useState('');

  // 全フォームデータ
  const [formData, setFormData] = useState({
    // --- 既存の項目 (必須にしたい項目は必要に応じて追加) ---
    companyName: '',       // 会社名 (例: ここを必須扱いする)
    scale: '',             // 従業員規模
    location: '',          // 所在地
    email: '',             // メールアドレス
    industry: '',          // 業種

    // --- 追加項目 ---
    // Step1: 顧客情報・検索キー
    kPublicationName: '',
    kPublicationNameFuri: '',
    kIndustryCode: '',
    kStockStatus: '',
    kHeadOfficePref: '',
    kBranchOfficePref: '',
    kEmployeeNumber: '',

    // Step2: 雇用促進法明示項目
    newGradHirePast3: '',
    newGradLeavePast3: '',
    newGradHireGenderPast3: '',
    averageTenure: '',
    trainingInfo: '',
    selfDevSupport: '',
    mentorSystem: '',
    careerConsulting: '',
    internalCertification: '',
    avgOvertime: '',
    avgPaidLeave: '',
    childcareTargets: '',
    childcareTaken: '',
    womenRatio: '',

    // Step3: 企業基本情報
    logoImage: null,
    companyCatch: '',
    businessContent: '',
    hqPostalCode: '',
    hqAddress: '',
    hqPhoneNumber: '',

    // Step4: 会社概要
    capital: '',
    employeesInfo: '',
    sales: '',
  });

  // ステップ配列
  const steps = [
    '顧客情報・検索キー',
    '雇用促進法明示項目',
    '企業基本情報',
    '会社概要',
    '確認・送信',
  ];

  // ======================
  // (B) マウント時にプロフィール有無をチェック
  // ======================
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      // トークン無 => そのままフォーム表示可能
      setLoading(false);
      return;
    }

    // トークンあり => プロフィール存在確認
    axios.get(`${BACKEND_URL}/basic-info/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        console.log("GET /basic-info/=> ", res.data);
        // プロフィール取得成功 => すでに入力済み
        setProfileExists(true);
      })
      .catch((err) => {
        console.warn("No profile found or error => user can input form", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // ローディング中は簡易的に表示を止める
  if (loading) {
    return <div>Loading...</div>;
  }

  // プロフィールがある状態かつ フォームを表示していないかつ 未送信なら -> ホームへ飛ばす
  // ただし、ユーザーが明示的にフォームを表示したい場合は表示を許可する
  if (profileExists && !showForm && !isFormSubmitted) {
    // プロフィールが存在してもフォームへのアクセスを完全にブロックしない
    // navigate('/home');
    // return null;
  }

  // フォーム送信完了後の自動遷移を防ぐ
  if (isFormSubmitted && activeStep < steps.length - 1) {
    console.log('Form submitted but not on final step - preventing auto navigation');
    // setIsFormSubmitted(false); // これを実行すると無限ループの可能性
  }

  // ======================
  // イベントハンドラ
  // ======================
  const handleShowForm = () => {
    setShowForm(true);
  };

  // ロックされた機能がクリックされた時
  const handleLockedFeatureClick = (featureName) => {
    if (!profileExists && !isFormSubmitted) {
      setLockedFeatureModal({
        open: true,
        featureName: featureName,
      });
    }
  };

  // モーダルを閉じる
  const handleCloseModal = () => {
    setLockedFeatureModal({
      open: false,
      featureName: '',
    });
  };

  // モーダルから基本情報入力へ
  const handleModalToForm = () => {
    handleCloseModal();
    setShowForm(true);
  };

  // 入力変更時
  const handleChangeInput = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      // ファイルアップロードの場合
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // ステップを進む
  const handleNext = () => {
    console.log('handleNext called, activeStep:', activeStep);
    console.log('Current formData:', formData);
    
    // 必須項目をチェックする (例: 会社名が空ならエラー)
    setErrorMsg('');

    // 例: Step 0 (一番最初) で会社名を必須にする
    if (activeStep === 0) {
      // 会社名が空?
      if (!formData.companyName.trim()) {
        setErrorMsg('【会社名】が未入力です。');
        return; // 進まない
      }
    }

    // Step 3（会社概要）から Step 4（確認）に進む場合、
    // データを保存してから確認画面へ
    if (activeStep === 3) {
      console.log('Moving from Step 3 to Step 4 (confirmation)');
      // 会社概要のデータが formData に保存されていることを確認
      console.log('Company overview data:', {
        capital: formData.capital,
        employeesInfo: formData.employeesInfo,
        sales: formData.sales
      });
    }

    // エラーなければ次へ
    console.log('Moving to step:', activeStep + 1);
    setActiveStep((prev) => prev + 1);
  };

  // ステップを戻る
  const handleBack = () => {
    setErrorMsg('');
    setActiveStep((prev) => prev - 1);
  };

  // 最終ステップで送信ボタン
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    
    console.log('handleSubmit called, activeStep:', activeStep);
    console.log('formData at submit:', formData);

    // Step 4（確認・送信）でない場合は送信しない
    if (activeStep !== 4) {
      console.error('Submit called but not on final step!');
      return;
    }

    // もし追加の必須項目チェックをするならここで行う
    // 例: "kEmployeeNumber" が必須なら
    // if (!formData.kEmployeeNumber.trim()) {
    //   setErrorMsg('【従業員数】が未入力です。');
    //   return;
    // }

    // 送信データ作成（try-catchの外で定義してスコープ問題を解決）
    const requestData = {
        company_name: formData.companyName,
        scale: formData.scale,
        location: formData.location,
        industry: formData.industry,
        k_publication_name: formData.kPublicationName,
        k_publication_name_furi: formData.kPublicationNameFuri,
        k_industry_code: formData.kIndustryCode,
        k_stock_status: formData.kStockStatus,
        k_head_office_pref: formData.kHeadOfficePref,
        k_branch_office_pref: formData.kBranchOfficePref,
        k_employee_number: formData.kEmployeeNumber,
        new_grad_hire_past_3: formData.newGradHirePast3,
        new_grad_leave_past_3: formData.newGradLeavePast3,
        new_grad_hire_gender_past_3: formData.newGradHireGenderPast3,
        average_tenure: formData.averageTenure,
        training_info: formData.trainingInfo,
        self_dev_support: formData.selfDevSupport,
        mentor_system: formData.mentorSystem,
        career_consulting: formData.careerConsulting,
        internal_certification: formData.internalCertification,
        avg_overtime: formData.avgOvertime,
        avg_paid_leave: formData.avgPaidLeave,
        childcare_targets: formData.childcareTargets,
        childcare_taken: formData.childcareTaken,
        women_ratio: formData.womenRatio,
        company_catch: formData.companyCatch,
        business_content: formData.businessContent,
        hq_postal_code: formData.hqPostalCode,
        hq_address: formData.hqAddress,
        hq_phone_number: formData.hqPhoneNumber,
        capital: formData.capital,
        employees_info: formData.employeesInfo,
        sales: formData.sales,
    };

    try {
      // APIリクエスト
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${BACKEND_URL}/basic-info/`,
        requestData,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          withCredentials: true,
        }
      );
      console.log('送信データ:', requestData);
      console.log('formData全体:', formData);
      console.log('サーバーからのレスポンス:', response.data);

      // 送信完了
      console.log('Setting isFormSubmitted to true');
      setIsFormSubmitted(true);
    } catch (error) {
      console.error('エラー詳細:', error.response?.data);
      console.error('送信したデータ:', requestData);
      console.error(error);
      alert('送信中にエラーが発生しました: ' + (error.response?.data?.detail || error.message));
    }
  };

  // 入力完了後のボタン => /home
  const handleNavigateHome = () => {
    navigate('/home');
  };

  // ステップ別フォーム内容
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* 基本情報セクション */}
            <Box
              sx={{
                p: 3,
                backgroundColor: bg(theme).card,
                borderRadius: 2,
                border: `2px solid ${adaptiveColor(theme, '#e8f4fd', 'rgba(33,150,243,0.15)')}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <BusinessIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                  基本情報
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="会社名"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChangeInput}
                    fullWidth
                    required
                    helperText="例：株式会社サンプル"
                    sx={{
                      backgroundColor: formData.companyName.trim() === '' ? adaptiveColor(theme, '#fff8f8', 'rgba(60,30,30,0.3)') : bg(theme).card,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                      '& .MuiInputLabel-root': {
                        '&::after': {
                          content: '" *"',
                          color: theme.palette.error.main,
                          fontWeight: 'bold',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="従業員規模"
                    name="scale"
                    value={formData.scale}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：50名、100〜200名、500名以上など"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="所在地"
                    name="location"
                    value={formData.location}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：東京都千代田区、大阪府大阪市など"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="メールアドレス"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：info@sample.co.jp"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="業種"
                    name="industry"
                    value={formData.industry}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：IT・ソフトウェア、製造業、小売業など"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* マイナビ情報セクション */}
            <Box
              sx={{
                p: 3,
                backgroundColor: bg(theme).card,
                borderRadius: 2,
                border: `2px solid ${adaptiveColor(theme, '#fff3e0', 'rgba(255,152,0,0.15)')}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <WorkIcon sx={{ color: '#FFA726', fontSize: 24 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#FFA726' }}>
                  募集媒体掲載情報
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  （求人媒体掲載時に使用）
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="掲載社名"
                    name="kPublicationName"
                    value={formData.kPublicationName}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：株式会社サンプル（募集媒体に表示される会社名）"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="掲載社名(フリガナ)"
                    name="kPublicationNameFuri"
                    value={formData.kPublicationNameFuri}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：カブシキガイシャサンプル"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl 
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  >
                    <InputLabel>業種コード</InputLabel>
                    <Select
                      label="業種コード"
                      name="kIndustryCode"
                      value={formData.kIndustryCode}
                      onChange={handleChangeInput}
                    >
                      <MenuItem value="">選択してください</MenuItem>
                      <MenuItem value="01">01 - 農業</MenuItem>
                      <MenuItem value="02">02 - 林業</MenuItem>
                      <MenuItem value="03">03 - 漁業（水産養殖業を除く）</MenuItem>
                      <MenuItem value="04">04 - 水産養殖業</MenuItem>
                      <MenuItem value="05">05 - 鉱業，採石業，砂利採取業</MenuItem>
                      <MenuItem value="06">06 - 総合工事業</MenuItem>
                      <MenuItem value="07">07 - 職別工事業(設備工事業を除く)</MenuItem>
                      <MenuItem value="08">08 - 設備工事業</MenuItem>
                      <MenuItem value="09">09 - 食料品製造業</MenuItem>
                      <MenuItem value="10">10 - 飲料・たばこ・飼料製造業</MenuItem>
                      <MenuItem value="11">11 - 繊維工業</MenuItem>
                      <MenuItem value="12">12 - 木材・木製品製造業（家具を除く）</MenuItem>
                      <MenuItem value="13">13 - 家具・装備品製造業</MenuItem>
                      <MenuItem value="14">14 - パルプ・紙・紙加工品製造業</MenuItem>
                      <MenuItem value="15">15 - 印刷・同関連業</MenuItem>
                      <MenuItem value="16">16 - 化学工業</MenuItem>
                      <MenuItem value="17">17 - 石油製品・石炭製品製造業</MenuItem>
                      <MenuItem value="18">18 - プラスチック製品製造業</MenuItem>
                      <MenuItem value="19">19 - ゴム製品製造業</MenuItem>
                      <MenuItem value="20">20 - なめし革・同製品・毛皮製造業</MenuItem>
                      <MenuItem value="21">21 - 窯業・土石製品製造業</MenuItem>
                      <MenuItem value="22">22 - 鉄鋼業</MenuItem>
                      <MenuItem value="23">23 - 非鉄金属製造業</MenuItem>
                      <MenuItem value="24">24 - 金属製品製造業</MenuItem>
                      <MenuItem value="25">25 - はん用機械器具製造業</MenuItem>
                      <MenuItem value="26">26 - 生産用機械器具製造業</MenuItem>
                      <MenuItem value="27">27 - 業務用機械器具製造業</MenuItem>
                      <MenuItem value="28">28 - 電子部品・デバイス・電子回路製造業</MenuItem>
                      <MenuItem value="29">29 - 電気機械器具製造業</MenuItem>
                      <MenuItem value="30">30 - 情報通信機械器具製造業</MenuItem>
                      <MenuItem value="31">31 - 輸送用機械器具製造業</MenuItem>
                      <MenuItem value="32">32 - その他の製造業</MenuItem>
                      <MenuItem value="33">33 - 電気業</MenuItem>
                      <MenuItem value="34">34 - ガス業</MenuItem>
                      <MenuItem value="35">35 - 熱供給業</MenuItem>
                      <MenuItem value="36">36 - 水道業</MenuItem>
                      <MenuItem value="37">37 - 通信業</MenuItem>
                      <MenuItem value="38">38 - 放送業</MenuItem>
                      <MenuItem value="39">39 - 情報サービス業</MenuItem>
                      <MenuItem value="40">40 - インターネット附随サービス業</MenuItem>
                      <MenuItem value="41">41 - 映像・音声・文字情報制作業</MenuItem>
                      <MenuItem value="42">42 - 鉄道業</MenuItem>
                      <MenuItem value="43">43 - 道路旅客運送業</MenuItem>
                      <MenuItem value="44">44 - 道路貨物運送業</MenuItem>
                      <MenuItem value="45">45 - 水運業</MenuItem>
                      <MenuItem value="46">46 - 航空運輸業</MenuItem>
                      <MenuItem value="47">47 - 倉庫業</MenuItem>
                      <MenuItem value="48">48 - 運輸に附帯するサービス業</MenuItem>
                      <MenuItem value="49">49 - 郵便業（信書便事業を含む）</MenuItem>
                      <MenuItem value="50">50 - 各種商品卸売業</MenuItem>
                      <MenuItem value="51">51 - 繊維・衣服等卸売業</MenuItem>
                      <MenuItem value="52">52 - 飲食料品卸売業</MenuItem>
                      <MenuItem value="53">53 - 建築材料，鉱物・金属材料等卸売業</MenuItem>
                      <MenuItem value="54">54 - 機械器具卸売業</MenuItem>
                      <MenuItem value="55">55 - その他の卸売業</MenuItem>
                      <MenuItem value="56">56 - 各種商品小売業</MenuItem>
                      <MenuItem value="57">57 - 織物・衣服・身の回り品小売業</MenuItem>
                      <MenuItem value="58">58 - 飲食料品小売業</MenuItem>
                      <MenuItem value="59">59 - 機械器具小売業</MenuItem>
                      <MenuItem value="60">60 - その他の小売業</MenuItem>
                      <MenuItem value="61">61 - 無店舗小売業</MenuItem>
                      <MenuItem value="62">62 - 銀行業</MenuItem>
                      <MenuItem value="63">63 - 協同組織金融業</MenuItem>
                      <MenuItem value="64">64 - 貸金業，クレジットカード業等非預金信用機関</MenuItem>
                      <MenuItem value="65">65 - 金融商品取引業，商品先物取引業</MenuItem>
                      <MenuItem value="66">66 - 補助的金融業等</MenuItem>
                      <MenuItem value="67">67 - 保険業（保険媒介代理業，保険サービス業を含む）</MenuItem>
                      <MenuItem value="68">68 - 不動産取引業</MenuItem>
                      <MenuItem value="69">69 - 不動産賃貸業・管理業</MenuItem>
                      <MenuItem value="70">70 - 物品賃貸業</MenuItem>
                      <MenuItem value="71">71 - 学術・開発研究機関</MenuItem>
                      <MenuItem value="72">72 - 専門サービス業（他に分類されないもの）</MenuItem>
                      <MenuItem value="73">73 - 広告業</MenuItem>
                      <MenuItem value="74">74 - 技術サービス業（他に分類されないもの）</MenuItem>
                      <MenuItem value="75">75 - 宿泊業</MenuItem>
                      <MenuItem value="76">76 - 飲食店</MenuItem>
                      <MenuItem value="77">77 - 持ち帰り・配達飲食サービス業</MenuItem>
                      <MenuItem value="78">78 - 洗濯・理容・美容・浴場業</MenuItem>
                      <MenuItem value="79">79 - その他の生活関連サービス業</MenuItem>
                      <MenuItem value="80">80 - 娯楽業</MenuItem>
                      <MenuItem value="81">81 - 学校教育</MenuItem>
                      <MenuItem value="82">82 - その他の教育，学習支援業</MenuItem>
                      <MenuItem value="83">83 - 医療業</MenuItem>
                      <MenuItem value="84">84 - 保健衛生</MenuItem>
                      <MenuItem value="85">85 - 社会保険・社会福祉・介護事業</MenuItem>
                      <MenuItem value="86">86 - 郵便局</MenuItem>
                      <MenuItem value="87">87 - 協同組合（他に分類されないもの）</MenuItem>
                      <MenuItem value="88">88 - 廃棄物処理業</MenuItem>
                      <MenuItem value="89">89 - 自動車整備業</MenuItem>
                      <MenuItem value="90">90 - 機械等修理業</MenuItem>
                      <MenuItem value="91">91 - 職業紹介・労働者派遣業</MenuItem>
                      <MenuItem value="92">92 - その他の事業サービス業</MenuItem>
                      <MenuItem value="93">93 - 政治・経済・文化団体</MenuItem>
                      <MenuItem value="94">94 - 宗教</MenuItem>
                      <MenuItem value="95">95 - その他のサービス業</MenuItem>
                      <MenuItem value="96">96 - 外国公務</MenuItem>
                      <MenuItem value="97">97 - 国家公務</MenuItem>
                      <MenuItem value="98">98 - 地方公務</MenuItem>
                      <MenuItem value="99">99 - 分類不能の産業</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="株式公開状況"
                    name="kStockStatus"
                    value={formData.kStockStatus}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：上場、非上場、準備中など"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="本社所在地(都道府県)"
                    name="kHeadOfficePref"
                    value={formData.kHeadOfficePref}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：東京都、大阪府、愛知県など"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="事業所所在地(都道府県)"
                    name="kBranchOfficePref"
                    value={formData.kBranchOfficePref}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：東京都、大阪府（複数の場合：東京都、大阪府、愛知県）"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="従業員数"
                    name="kEmployeeNumber"
                    value={formData.kEmployeeNumber}
                    onChange={(e) => handleNumericInput(e, (value) => setFormData(prev => ({ ...prev, kEmployeeNumber: value })))}
                    fullWidth
                    placeholder="例: 100"
                    helperText={formData.kEmployeeNumber ? formatEmployee(formData.kEmployeeNumber) : '数値のみ入力してください（例: 100）'}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* 注意事項 */}
            <Box
              sx={{
                p: 2,
                backgroundColor: statusBg(theme).warning,
                borderRadius: 2,
                border: '1px solid #ffb74d',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <InfoIcon sx={{ color: '#ff9800', fontSize: 24 }} />
              <Typography variant="body2" color="#ef6c00">
                これらは青少年雇用促進法に基づく法定開示項目です。正確な情報の入力をお願いします。
              </Typography>
            </Box>

            {/* A) 募集・採用セクション */}
            <Box
              sx={{
                p: 3,
                backgroundColor: bg(theme).card,
                borderRadius: 2,
                border: `2px solid ${adaptiveColor(theme, '#e8f5e8', 'rgba(76,175,80,0.15)')}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <PeopleIcon sx={{ color: '#4caf50', fontSize: 24 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                  A) 募集・採用に関する状況
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="過去３年間の新卒採用者数"
                    name="newGradHirePast3"
                    value={formData.newGradHirePast3}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：20名（2021年度：8名、2022年度：6名、2023年度：6名）"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="過去３年間の新卒離職者数"
                    name="newGradLeavePast3"
                    value={formData.newGradLeavePast3}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：3名（2021年度：1名、2022年度：1名、2023年度：1名）"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="過去３年間の新卒採用者数（男女別）"
                    name="newGradHireGenderPast3"
                    value={formData.newGradHireGenderPast3}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：男性12名、女性8名"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="平均勤続年数"
                    name="averageTenure"
                    value={formData.averageTenure}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：5.2年"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* B) 職業能力開発セクション */}
            <Box
              sx={{
                p: 3,
                backgroundColor: bg(theme).card,
                borderRadius: 2,
                border: `2px solid ${border(theme).light}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <WorkIcon sx={{ color: '#2196f3', fontSize: 24 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2196f3' }}>
                  B) 職業能力の開発・向上に関する状況
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="研修の有無及び内容"
                    name="trainingInfo"
                    value={formData.trainingInfo}
                    onChange={handleChangeInput}
                    fullWidth
                    multiline
                    rows={2}
                    helperText="例：新入社員研修（3ヶ月）、OJT研修、スキルアップ研修など"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="自己啓発支援の有無及び内容"
                    name="selfDevSupport"
                    value={formData.selfDevSupport}
                    onChange={handleChangeInput}
                    fullWidth
                    multiline
                    rows={2}
                    helperText="例：資格取得支援制度、セミナー参加費補助、書籍購入補助など"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="メンター制度の有無"
                    name="mentorSystem"
                    value={formData.mentorSystem}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：あり（新入社員に先輩社員を1名配置）、なし"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="キャリアコンサルティング制度の有無及び内容"
                    name="careerConsulting"
                    value={formData.careerConsulting}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：年2回の個別面談、キャリア相談窓口の設置、なし"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="社内検定等の制度の有無及び内容"
                    name="internalCertification"
                    value={formData.internalCertification}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：社内技術検定制度、なし"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* C) 雇用管理セクション */}
            <Box
              sx={{
                p: 3,
                backgroundColor: bg(theme).card,
                borderRadius: 2,
                border: `2px solid ${adaptiveColor(theme, '#fce4ec', 'rgba(233,30,99,0.15)')}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <CategoryIcon sx={{ color: '#e91e63', fontSize: 24 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#e91e63' }}>
                  C) 雇用管理に関する状況
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="前年度の月平均所定外労働時間の実績"
                    name="avgOvertime"
                    value={formData.avgOvertime}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：10.5時間"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="前年度の有給休暇の平均取得"
                    name="avgPaidLeave"
                    value={formData.avgPaidLeave}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：12.3日"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="前年度の育児休業取得対象者数（男女別）"
                    name="childcareTargets"
                    value={formData.childcareTargets}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：男性3名、女性5名"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="前年度の育児休業取得者数（男女別）"
                    name="childcareTaken"
                    value={formData.childcareTaken}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：男性2名、女性5名"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="役員・管理職に占める女性の割合"
                    name="womenRatio"
                    value={formData.womenRatio}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：30%"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* 企業基本情報セクション */}
            <Box
              sx={{
                p: 3,
                backgroundColor: bg(theme).card,
                borderRadius: 2,
                border: `2px solid ${adaptiveColor(theme, '#f3e5f5', 'rgba(156,39,176,0.15)')}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <BusinessIcon sx={{ color: '#9c27b0', fontSize: 24 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                  企業基本情報
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  （企業プロフィール用）
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="企業キャッチコピー"
                    name="companyCatch"
                    value={formData.companyCatch}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：あなたのアイデアが世界を変える、挑戦し続ける企業など"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="事業内容"
                    name="businessContent"
                    value={formData.businessContent}
                    onChange={handleChangeInput}
                    fullWidth
                    multiline
                    rows={3}
                    helperText="例：Webアプリケーション開発、システムコンサルティング、IT教育事業など"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="本社郵便番号"
                    name="hqPostalCode"
                    value={formData.hqPostalCode}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：100-0001"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="本社所在地"
                    name="hqAddress"
                    value={formData.hqAddress}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：東京都千代田区千代田1-1-1 ○○ビル5F"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="本社電話番号"
                    name="hqPhoneNumber"
                    value={formData.hqPhoneNumber}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：03-1234-5678"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* 会社概要セクション */}
            <Box
              sx={{
                p: 3,
                backgroundColor: bg(theme).card,
                borderRadius: 2,
                border: `2px solid ${adaptiveColor(theme, '#e8f5e8', 'rgba(76,175,80,0.15)')}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <CategoryIcon sx={{ color: '#4caf50', fontSize: 24 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                  会社概要
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  （規模・実績情報）
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="資本金"
                    name="capital"
                    value={formData.capital}
                    onChange={(e) => handleNumericInput(e, (value) => setFormData(prev => ({ ...prev, capital: value })))}
                    fullWidth
                    placeholder="例: 100000000"
                    helperText={formData.capital ? formatCurrency(formData.capital) : '※ 半角数字のみ入力してください（例: 100000000）'}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="従業員情報"
                    name="employeesInfo"
                    value={formData.employeesInfo}
                    onChange={handleChangeInput}
                    fullWidth
                    helperText="例：正社員100名、パート20名（2024年4月現在）"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="売上高"
                    name="sales"
                    value={formData.sales}
                    onChange={(e) => handleNumericInput(e, (value) => setFormData(prev => ({ ...prev, sales: value })))}
                    fullWidth
                    placeholder="例: 5000000000"
                    helperText={formData.sales ? formatCurrency(formData.sales) : '※ 半角数字のみ入力してください（例: 5000000000）'}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        );

      case 4:
        // 確認・送信ステップ
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box
              sx={{
                p: 3,
                backgroundColor: bg(theme).card,
                borderRadius: 2,
                border: `2px solid ${border(theme).light}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <InfoIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                  入力内容の確認
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                以下の内容で登録します。よろしければ「入力完了」ボタンを押してください。
              </Typography>

              {/* 全項目の確認表示 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                
                {/* 基本情報セクション */}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 2 }}>
                    基本情報
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pl: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">会社名</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {formData.companyName || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">従業員規模</Typography>
                      <Typography variant="body1">
                        {formData.scale || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">所在地</Typography>
                      <Typography variant="body1">
                        {formData.location || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">メールアドレス</Typography>
                      <Typography variant="body1">
                        {formData.email || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">業種</Typography>
                      <Typography variant="body1">
                        {formData.industry || '（未入力）'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* 募集媒体掲載情報セクション */}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#FFA726', mb: 2 }}>
                    募集媒体掲載情報
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pl: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">掲載社名</Typography>
                      <Typography variant="body1">
                        {formData.kPublicationName || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">掲載社名(フリガナ)</Typography>
                      <Typography variant="body1">
                        {formData.kPublicationNameFuri || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">業種コード</Typography>
                      <Typography variant="body1">
                        {formData.kIndustryCode || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">株式公開状況</Typography>
                      <Typography variant="body1">
                        {formData.kStockStatus || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">本社所在地(都道府県)</Typography>
                      <Typography variant="body1">
                        {formData.kHeadOfficePref || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">事業所所在地(都道府県)</Typography>
                      <Typography variant="body1">
                        {formData.kBranchOfficePref || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">従業員数</Typography>
                      <Typography variant="body1">
                        {formData.kEmployeeNumber ? formatEmployee(formData.kEmployeeNumber) : '（未入力）'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* 雇用促進法明示項目セクション */}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4caf50', mb: 2 }}>
                    雇用促進法明示項目
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pl: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">過去３年間の新卒採用者数</Typography>
                      <Typography variant="body1">
                        {formData.newGradHirePast3 || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">過去３年間の新卒離職者数</Typography>
                      <Typography variant="body1">
                        {formData.newGradLeavePast3 || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">過去３年間の新卒採用者数（男女別）</Typography>
                      <Typography variant="body1">
                        {formData.newGradHireGenderPast3 || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">平均勤続年数</Typography>
                      <Typography variant="body1">
                        {formData.averageTenure || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">研修の有無及び内容</Typography>
                      <Typography variant="body1">
                        {formData.trainingInfo || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">自己啓発支援の有無及び内容</Typography>
                      <Typography variant="body1">
                        {formData.selfDevSupport || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">メンター制度の有無</Typography>
                      <Typography variant="body1">
                        {formData.mentorSystem || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">キャリアコンサルティング制度の有無及び内容</Typography>
                      <Typography variant="body1">
                        {formData.careerConsulting || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">社内検定等の制度の有無及び内容</Typography>
                      <Typography variant="body1">
                        {formData.internalCertification || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">前年度の月平均所定外労働時間の実績</Typography>
                      <Typography variant="body1">
                        {formData.avgOvertime || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">前年度の有給休暇の平均取得</Typography>
                      <Typography variant="body1">
                        {formData.avgPaidLeave || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">前年度の育児休業取得対象者数（男女別）</Typography>
                      <Typography variant="body1">
                        {formData.childcareTargets || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">前年度の育児休業取得者数（男女別）</Typography>
                      <Typography variant="body1">
                        {formData.childcareTaken || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">役員・管理職に占める女性の割合</Typography>
                      <Typography variant="body1">
                        {formData.womenRatio || '（未入力）'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* 企業基本情報セクション */}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#9c27b0', mb: 2 }}>
                    企業基本情報
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pl: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">企業キャッチコピー</Typography>
                      <Typography variant="body1">
                        {formData.companyCatch || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">事業内容</Typography>
                      <Typography variant="body1">
                        {formData.businessContent || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">本社郵便番号</Typography>
                      <Typography variant="body1">
                        {formData.hqPostalCode || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">本社所在地</Typography>
                      <Typography variant="body1">
                        {formData.hqAddress || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">本社電話番号</Typography>
                      <Typography variant="body1">
                        {formData.hqPhoneNumber || '（未入力）'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* 会社概要セクション */}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4caf50', mb: 2 }}>
                    会社概要
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pl: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">資本金</Typography>
                      <Typography variant="body1">
                        {formData.capital ? formatCurrency(formData.capital) : '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">従業員情報</Typography>
                      <Typography variant="body1">
                        {formData.employeesInfo || '（未入力）'}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary">売上高</Typography>
                      <Typography variant="body1">
                        {formData.sales ? formatCurrency(formData.sales) : '（未入力）'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: bg(theme).card,
      }}
    >
      {/* メインコンテンツ */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, md: 0 },
        }}
      >
        <Container
          component="main"
          maxWidth={false}
          sx={{
            maxWidth: 'none',
            py: { xs: 2, sm: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Paper elevation={0} sx={{ backgroundColor: 'transparent', width: '100%' }}>
            {/* 1) フォーム未表示 & 未送信 */}
            {(!showForm && !isFormSubmitted) && (
              <>
                <Box
                  component={motion.div}
                  {...fadeIn(0, 0.6)}
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: { xs: 180, sm: 300, md: 450 },
                    mb: { xs: 3, sm: 4, md: 6 },
                    backgroundImage: `url(${topImage})`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                />

                <Box
                  component={motion.div}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                >
                  <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ justifyContent: 'center' }}>
                    <Grid item xs={6} sm={6} md={3}>
                      <HoverCard
                        icon={<AssignmentIcon sx={{ fontSize: { xs: 36, sm: 48 }, mb: 1, color: theme.palette.primary.main }} />}
                        title="採用計画アシスト"
                        description="効率的な採用計画の立案"
                        hoverIconColor="red"
                        isLocked={!profileExists && !isFormSubmitted}
                        onClick={() => handleLockedFeatureClick('採用計画アシスト')}
                        isCompact={isSmDown}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={3}>
                      <HoverCard
                        icon={<CampaignIcon sx={{ fontSize: { xs: 36, sm: 48 }, mb: 1, color: theme.palette.primary.main }} />}
                        title="募集媒体選定"
                        description="適切な媒体で応募数を伸ばす"
                        hoverIconColor="blue"
                        isLocked={!profileExists && !isFormSubmitted}
                        onClick={() => handleLockedFeatureClick('募集媒体選定')}
                        isCompact={isSmDown}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={3}>
                      <HoverCard
                        icon={<DescriptionIcon sx={{ fontSize: { xs: 36, sm: 48 }, mb: 1, color: theme.palette.primary.main }} />}
                        title="求人票の自動作成"
                        description="AIで魅力ある求人を素早く"
                        hoverIconColor="yellow"
                        isLocked={!profileExists && !isFormSubmitted}
                        onClick={() => handleLockedFeatureClick('求人票の自動作成')}
                        isCompact={isSmDown}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={3}>
                      <HoverCard
                        icon={<QuestionAnswerIcon sx={{ fontSize: { xs: 36, sm: 48 }, mb: 1, color: theme.palette.primary.main }} />}
                        title="面接サポート"
                        description="スムーズな面接と評価"
                        hoverIconColor="green"
                        isLocked={!profileExists && !isFormSubmitted}
                        onClick={() => handleLockedFeatureClick('面接サポート')}
                        isCompact={isSmDown}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box
                  component={motion.div}
                  {...fadeIn(0.4, 0.6)}
                  sx={{ mt: { xs: 4, sm: 6, md: 8 } }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: { xs: 1.5, sm: 2 },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                        fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' },
                        textAlign: 'center',
                        px: 2,
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { opacity: 1 },
                          '50%': { opacity: 0.7 },
                        },
                      }}
                    >
                      まずは基本情報を入力して機能を解放しましょう
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleShowForm}
                      sx={{
                        px: { xs: 6, sm: 8, md: 10 },
                        py: { xs: 1.5, sm: 2, md: 2.5 },
                        borderRadius: 3,
                        fontWeight: 'bold',
                        fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                        backgroundColor: theme.palette.primary.main,
                        boxShadow: '0 6px 20px rgba(36, 155, 224, 0.4)',
                        animation: 'bounce 2s ease-in-out infinite',
                        '@keyframes bounce': {
                          '0%, 100%': { transform: 'translateY(0)' },
                          '50%': { transform: 'translateY(-8px)' },
                        },
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark,
                          boxShadow: '0 8px 24px rgba(36, 155, 224, 0.5)',
                          transform: 'scale(1.05)',
                          animation: 'none',
                        },
                      }}
                    >
                      基本情報を入力
                    </Button>
                  </Box>
                </Box>
              </>
            )}

            {/* 2) フォーム表示中 & 未送信 */}
            {showForm && !isFormSubmitted && (
              <Box
                component={motion.div}
                {...fadeIn(0.4, 0.6)}
                sx={{
                  width: { xs: '90%', sm: '80%', md: '70%' },
                  mt: 4,
                  mx: 'auto',
                  p: { xs: 3, sm: 4 },
                  textAlign: 'left',
                  backgroundColor: bg(theme).card,
                  borderRadius: 3,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                  border: `1px solid ${border(theme).default}`,
                }}
              >
                {/* 説明メッセージ */}
                <Box
                  sx={{
                    mb: 4,
                    p: 3,
                    backgroundColor: bg(theme).tinted,
                    borderRadius: 2,
                    border: `1px solid ${border(theme).light}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <InfoIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>
                      入力についてのご案内
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <StarIcon sx={{ fontSize: 16, color: theme.palette.error.main, mr: 0.5 }} />
                      が付いている項目は必須項目です。その他の項目は後から設定画面で変更・追加が可能です。
                    </Typography>
                  </Box>
                </Box>

                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>

                {/* ★ エラーメッセージをボタン上に表示 */}
                {errorMsg && (
                  <Typography
                    variant="body1"
                    color="error"
                    sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}
                  >
                    {errorMsg}
                  </Typography>
                )}

                <Box
                  component="form"
                  onSubmit={(e) => {
                    console.log('Form onSubmit triggered, activeStep:', activeStep, 'steps.length:', steps.length);
                    // フォームの自動送信を常に防ぐ（ボタンのonClickで明示的に制御）
                    e.preventDefault();
                    console.log('Form submit prevented - using button onClick instead');
                  }}
                  sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                >
                  {renderStepContent(activeStep)}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                    <Button
                      disabled={activeStep === 0}
                      onClick={handleBack}
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        fontWeight: 'bold',
                        px: 4,
                        py: 1.5,
                        border: `2px solid ${border(theme).default}`,
                        color: textColors(theme).muted,
                        '&:hover': {
                          border: '2px solid #249BE0',
                          color: theme.palette.primary.main,
                          backgroundColor: bg(theme).tinted,
                        },
                        '&:disabled': {
                          border: `2px solid ${border(theme).default}`,
                          color: textColors(theme).disabled,
                        },
                      }}
                    >
                      戻る
                    </Button>

                    {(() => {
                      const isLastStep = activeStep === steps.length - 1;
                      console.log('Button render - activeStep:', activeStep, 'steps.length - 1:', steps.length - 1, 'isLastStep:', isLastStep);
                      console.log('Showing button type:', isLastStep ? 'SUBMIT (入力完了)' : 'NEXT (次へ)');
                      return isLastStep;
                    })() ? (
                      <Button
                        onClick={(e) => {
                          console.log('Submit button clicked on Step 4');
                          handleSubmit(e);
                        }}
                        variant="contained"
                        sx={{
                          borderRadius: 3,
                          fontWeight: 'bold',
                          px: 6,
                          py: 1.5,
                          backgroundColor: theme.palette.primary.main,
                          boxShadow: '0 4px 12px rgba(36, 155, 224, 0.3)',
                          '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                            boxShadow: '0 6px 16px rgba(36, 155, 224, 0.4)',
                            transform: 'translateY(-1px)',
                          },
                        }}
                      >
                        入力完了
                      </Button>
                    ) : (
                      <Button
                        onClick={handleNext}
                        variant="contained"
                        sx={{
                          borderRadius: 3,
                          fontWeight: 'bold',
                          px: 6,
                          py: 1.5,
                          backgroundColor: theme.palette.primary.main,
                          boxShadow: '0 4px 12px rgba(36, 155, 224, 0.3)',
                          '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                            boxShadow: '0 6px 16px rgba(36, 155, 224, 0.4)',
                            transform: 'translateY(-1px)',
                          },
                        }}
                      >
                        次へ
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            )}

            {/* 3) 送信完了 */}
            {isFormSubmitted && activeStep === steps.length - 1 && (
              <Box
                component={motion.div}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                sx={{ mt: 8 }}
              >
                {/* 成功アイコンとメッセージ */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  {/* アニメーション付き成功アイコン */}
                  <Box
                    component={motion.div}
                    initial={{ scale: 0 }}
                    animate={{ scale: [0, 1.2, 1] }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      backgroundColor: '#4caf50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 8px 24px rgba(76, 175, 80, 0.4)',
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 60, color: '#fff' }} />
                  </Box>

                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 'bold',
                      letterSpacing: '0.5px',
                      textAlign: 'center',
                      color: textColors(theme).primary,
                    }}
                  >
                    基本情報の登録が完了しました！
                  </Typography>

                  {/* ロック解除アニメーション */}
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    sx={{
                      backgroundColor: bg(theme).card,
                      borderRadius: 3,
                      p: 4,
                      boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                      border: '2px solid #4caf50',
                      width: '100%',
                      maxWidth: 600,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
                      <LockOpenIcon sx={{ fontSize: 32, color: '#4caf50' }} />
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                        全ての機能が利用可能になりました！
                      </Typography>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: 2,
                            backgroundColor: bg(theme).tinted,
                            borderRadius: 2,
                            textAlign: 'center',
                            border: `1px solid ${border(theme).light}`,
                          }}
                        >
                          <AssignmentIcon sx={{ fontSize: 32, mb: 1, color: theme.palette.primary.main }} />
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            採用計画アシスト
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: 2,
                            backgroundColor: bg(theme).tinted,
                            borderRadius: 2,
                            textAlign: 'center',
                            border: `1px solid ${border(theme).light}`,
                          }}
                        >
                          <CampaignIcon sx={{ fontSize: 32, mb: 1, color: theme.palette.primary.main }} />
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            募集媒体選定
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: 2,
                            backgroundColor: bg(theme).tinted,
                            borderRadius: 2,
                            textAlign: 'center',
                            border: `1px solid ${border(theme).light}`,
                          }}
                        >
                          <DescriptionIcon sx={{ fontSize: 32, mb: 1, color: theme.palette.primary.main }} />
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            求人票の自動作成
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: 2,
                            backgroundColor: bg(theme).tinted,
                            borderRadius: 2,
                            textAlign: 'center',
                            border: `1px solid ${border(theme).light}`,
                          }}
                        >
                          <QuestionAnswerIcon sx={{ fontSize: 32, mb: 1, color: theme.palette.primary.main }} />
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            面接サポート
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  <Button
                    component={motion.button}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    variant="contained"
                    size="large"
                    onClick={handleNavigateHome}
                    sx={{
                      px: 8,
                      py: 2.5,
                      borderRadius: 3,
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      backgroundColor: theme.palette.primary.main,
                      boxShadow: '0 6px 20px rgba(36, 155, 224, 0.4)',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        boxShadow: '0 8px 24px rgba(36, 155, 224, 0.5)',
                      },
                    }}
                  >
                    ホーム画面へ進む
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        </Container>
      </Box>

      <Box sx={{ width: '100%' }}>
        <Footer />
      </Box>

      {/* ロック機能クリック時のモーダル */}
      <Dialog
        open={lockedFeatureModal.open}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            pb: 2,
          }}
        >
          <LockIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            この機能はロックされています
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body1" sx={{ fontSize: '1rem', lineHeight: 1.8 }}>
              <strong>「{lockedFeatureModal.featureName}」</strong>をご利用いただくには、まず基本情報の入力が必要です。
            </Typography>
            <Box
              sx={{
                backgroundColor: bg(theme).tinted,
                borderRadius: 2,
                p: 2,
                border: `1px solid ${border(theme).light}`,
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                基本情報を入力すると、以下の機能が利用可能になります：
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 3 }}>
                <li>
                  <Typography variant="body2">採用計画アシスト</Typography>
                </li>
                <li>
                  <Typography variant="body2">募集媒体選定</Typography>
                </li>
                <li>
                  <Typography variant="body2">求人票の自動作成</Typography>
                </li>
                <li>
                  <Typography variant="body2">面接サポート</Typography>
                </li>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseModal}
            sx={{
              borderRadius: 2,
              px: 3,
              color: textColors(theme).muted,
              '&:hover': {
                backgroundColor: bg(theme).tinted,
              },
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleModalToForm}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 4,
              fontWeight: 'bold',
              backgroundColor: theme.palette.primary.main,
              boxShadow: '0 4px 12px rgba(36, 155, 224, 0.3)',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: '0 6px 16px rgba(36, 155, 224, 0.4)',
              },
            }}
          >
            今すぐ基本情報を入力する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Landing;

/**
 * マウスオーバー時にアイコン色だけ変えるカード
 * - デフォルトアイコン色: #249BE0
 * - ホバー時: hoverIconColor
 * - isLocked: ロック状態を表示
 * - onClick: クリック時の処理
 * - isCompact: スマホ表示用のコンパクトモード
 */
function HoverCard({ icon, title, description, hoverIconColor, isLocked = false, onClick, isCompact = false }) {
  const theme = useTheme();
  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 2,
        boxShadow: 2,
        cursor: 'pointer',
        transition: 'transform 0.3s, box-shadow 0.3s, opacity 0.3s',
        backgroundColor: isLocked ? bg(theme).tinted : bg(theme).card,
        p: isCompact ? 2 : 3,
        position: 'relative',
        opacity: isLocked ? 0.7 : 1,
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: 4,
          opacity: isLocked ? 0.8 : 1,
          '& svg': {
            color: isLocked ? undefined : hoverIconColor,
          },
        },
      }}
    >
      {/* ロックバッジ */}
      {isLocked && (
        <Box
          sx={{
            position: 'absolute',
            top: isCompact ? 8 : 12,
            right: isCompact ? 8 : 12,
            backgroundColor: theme.palette.error.main,
            borderRadius: '12px',
            px: isCompact ? 1 : 1.5,
            py: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            boxShadow: '0 2px 8px rgba(255, 107, 107, 0.3)',
          }}
        >
          <LockIcon sx={{ fontSize: isCompact ? 12 : 14, color: '#fff' }} />
          {!isCompact && (
            <Typography variant="caption" sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.65rem' }}>
              要入力
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ mb: isCompact ? 0.5 : 1 }}>{icon}</Box>
      <Typography
        variant={isCompact ? 'body2' : 'h6'}
        sx={{
          fontWeight: 'medium',
          mb: isCompact ? 0.5 : 1,
          color: isLocked ? textColors(theme).muted : 'inherit',
          fontSize: isCompact ? '0.8rem' : undefined,
          textAlign: 'center',
          lineHeight: 1.3,
        }}
      >
        {title}
      </Typography>
      {!isCompact && (
        <Typography variant="body2" color={isLocked ? 'text.disabled' : 'text.secondary'}>
          {description}
        </Typography>
      )}

      {/* ロック状態の説明 */}
      {isLocked && !isCompact && (
        <Typography
          variant="caption"
          sx={{
            mt: 1.5,
            color: theme.palette.error.main,
            fontWeight: 'bold',
            fontSize: '0.7rem',
          }}
        >
          基本情報入力後に利用可能
        </Typography>
      )}

      {/* スマホ表示用の簡略ロック説明 */}
      {isLocked && isCompact && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            color: theme.palette.error.main,
            fontWeight: 'bold',
            fontSize: '0.6rem',
            textAlign: 'center',
          }}
        >
          要入力
        </Typography>
      )}
    </Card>
  );
}